import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

/**
 * Bundle tiers in the buy box (Titanium Wares, Horizon 3.3).
 *
 * The chosen tier decides how many units go to the cart and which size each unit has. Prices are
 * computed from the variants the block printed (price / compare-at per size) and the tier's extra
 * percentage; the gifts the block printed for that tier (one `.st-bundle__gift` row per unlocked slot,
 * cumulative, each with its variant id and value) are listed FREE with their value struck
 * through and counted into the "was" price. On submit the main product
 * form is intercepted (capture phase, before <product-form-component> and the offer boxes) and all
 * units, the gifts and any ticked paid add-on box are sent in ONE /cart/add.js request together
 * with the drawer section ids, then announced with the theme's CartAddEvent so the drawer opens once.
 * The store's automatic discounts (Buy 2 = 10%, Buy 3 = 15%) make the cart total match what the tiers
 * show; the gifts need no discount because each gift product is already priced $0.
 */
class StBundleComponent extends Component {
  requiredRefs = ['tiers'];
  #variants = [];
  #tiers = [];
  #form = null;
  #priceEl = null;
  static #bound = false;

  connectedCallback() {
    super.connectedCallback();
    try { this.#variants = JSON.parse(this.dataset.variants || '[]'); } catch { this.#variants = []; }
    try { this.#tiers = JSON.parse(this.dataset.tiers || '[]'); } catch { this.#tiers = []; }
    const section = this.closest('.shopify-section') || document;
    const comp = [...section.querySelectorAll('product-form-component')].find((f) => !f.closest('st-upsell-component') && !f.closest('st-bundle-component'));
    this.#form = comp ? comp.querySelector('form') : null;
    const wanted = String(this.dataset.defaultTier || '2');
    const initial = this.refs.tiers.find((r) => r.value === wanted) || this.refs.tiers[0];
    if (initial) initial.checked = true;
    // single unit: the size picked in the (now hidden) theme picker; a set: one of each size in order
    // (set of 2 = S + M, set of 3 = S + M + L; fewer sizes than units → the last size repeats)
    const current = this.#form?.querySelector('input[name="id"]')?.value;
    for (const box of this.querySelectorAll('[data-tier-sizes]')) {
      const selects = [...box.querySelectorAll('select.st-bundle__select')];
      selects.forEach((sel, i) => {
        const open = [...sel.options].filter((o) => !o.disabled);
        if (!open.length) return;
        if (selects.length === 1) { if (current && open.some((o) => o.value === current)) sel.value = current; return; }
        sel.value = open[Math.min(i, open.length - 1)].value;
      });
    }
    this.#render();
    document.addEventListener('change', this.#onAddonToggle);
    if (!StBundleComponent.#bound) {
      StBundleComponent.#bound = true;
      window.addEventListener('submit', StBundleComponent.onSubmit, true); // window capture runs before the theme's document-level delegation
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('change', this.#onAddonToggle);
  }

  /** a paid add-on box was ticked / unticked: the button total changes */
  #onAddonToggle = (event) => {
    if (event.target?.closest?.('st-upsell-component')) setTimeout(() => this.#syncButton(), 0);
  };

  /** the checked tier (1..3) */
  get tier() {
    const r = this.refs.tiers.find((x) => x.checked);
    return r ? Number(r.value) : 1;
  }

  /** tier config for n */
  #tierCfg(n) {
    return this.#tiers.find((t) => Number(t.qty) === n) || { qty: n, pct: 0, gifts: 0 };
  }

  /** cents off ONE unit at tier cfg: a fixed amount each (22/10/2026, the utensils) or the tier's % with Shopify's
   *  per-line rounding (the fraction of a cent is dropped: 79.95 x 10% -> 7.99) */
  #cut(price, cfg) {
    const off = Number(cfg.off || 0);
    if (off > 0) return Math.min(off, price);
    return Math.floor(price * Number(cfg.pct || 0) / 100 + 1e-9);
  }

  /** variant ids chosen for tier n (one per unit) */
  #chosen(n) {
    const box = this.querySelector(`[data-tier-sizes="${n}"]`);
    if (!box) return [];
    return [...box.querySelectorAll('select')].map((s) => Number(s.value)).filter(Boolean);
  }

  /** {now, was, off} in cents for tier n (off = the tier's extra % only) */
  /** the cheapest available size at tier n's %, per unit, with its own compare-at: the card's "From" price */
  #lowest(n) {
    const cfg = this.#tierCfg(n);
    let best = null;
    for (const v of this.#variants) {
      if (v.available === false) continue;
      const price = Number(v.price);
      const now = price - this.#cut(price, cfg);
      if (!best || now < best.now) best = { now, was: Number(v.compare || v.price) };
    }
    return best || { now: 0, was: 0 };
  }

  #totals(n) {
    const cfg = this.#tierCfg(n);
    let now = 0, was = 0, off = 0;
    for (const id of this.#chosen(n)) {
      const v = this.#variants.find((x) => Number(x.id) === id);
      if (!v) continue;
      const cut = this.#cut(Number(v.price), cfg);
      now += Number(v.price) - cut;
      off += cut;
      was += Number(v.compare || v.price);
    }
    // the struck price is the product's own compare-at only: the gifts have their own "worth" chip and
    // their own FREE rows, so folding them in here would inflate what looks like the old price
    return { now, was, off };
  }

  /** the sizes chosen for tier n as one line: "S + M + L", or "24 + 28 cm" when every title carries cm */
  #mix(n) {
    const titles = this.#chosen(n).map((id) => String(this.#variants.find((x) => Number(x.id) === id)?.title || '')).filter(Boolean);
    const cm = titles.map((t) => t.match(/\((\d+(?:\.\d+)?)\s*cm\)/i)?.[1]);
    return cm.every(Boolean) ? `${cm.join(' + ')} cm` : titles.join(' + ');
  }

  /** gifts unlocked at tier n, as printed by the block: [{id, value}] (value in cents) */
  #gifts(n) {
    return [...this.querySelectorAll(`[data-tier="${n}"] .st-bundle__gift[data-gift-id]`)]
      .map((el) => ({ id: Number(el.dataset.giftId || 0), value: Number(el.dataset.giftValue || 0) }))
      .filter((g) => g.id);
  }

  select() { this.#render(); }
  resize() { this.#render(); }

  #render() {
    const active = this.tier;
    for (const t of [1, 2, 3]) {
      const { now, was, off } = this.#totals(t);
      const nowEl = this.querySelector(`[data-tier-price="${t}"]`);
      const wasEl = this.querySelector(`[data-tier-compare="${t}"]`);
      // "From $x": the cheapest size at this tier's % (per unit from 2 up); the button keeps the exact total
      const low = this.#lowest(t);
      // 30/10/2026 owner: the TOTAL for t units of the cheapest size (a per-unit price looked lower than Buy 1)
      if (nowEl) nowEl.innerHTML = '<span class="st-bundle__from">From</span> ' + this.#money(low.now * t);
      if (wasEl) { wasEl.textContent = low.was > low.now ? this.#money(low.was * t) : ''; wasEl.hidden = !(low.was > low.now); }
      // sizes differ in price, so no "each" price: the outlined badge says "Extra n% off", this chip the dollars
      const saveEl = this.querySelector(`[data-tier-save="${t}"]`);
      // only the figure is red: the tier card already carries six red marks, so a whole red line would
      // flatten the hierarchy the gift chip depends on
      if (saveEl) saveEl.innerHTML = off > 0 ? `Save <span class="st-bundle__amt">${this.#money(off)}</span><span class="st-bundle__vs"> vs buying separately</span>` : '';
      const mixEl = this.querySelector(`[data-tier-mix="${t}"]`);
      if (mixEl) mixEl.textContent = this.#mix(t);
      const details = this.querySelector(`[data-tier-details="${t}"]`);
      if (details) details.hidden = t !== active;
      const sizes = this.querySelector(`[data-tier-sizes="${t}"]`);
      if (sizes) sizes.hidden = t !== active;
    }
    // keep the theme's hidden variant input on the first chosen size (price block, sticky bar)
    const first = this.#chosen(active)[0];
    const input = this.#form?.querySelector('input[name="id"]');
    if (input && first && String(first) !== input.value) input.value = String(first);
    this.#renderGifts(active);
    this.#syncButton();
  }

  /** 29/09/2026 offer v2: the gifts grid (tick / lock per tile, "n of m unlocked", hint) and the "You save" box */
  #renderGifts(n) {
    const tiles = [...this.querySelectorAll('.st-bundle__gb-tile')];
    let on = 0;
    let top = 1;
    const grid = this.querySelector('.st-bundle__gb-grid');
    const first = !this.#gbReady;
    if (first && grid) {
      // first paint: take the state without playing the unlock; a tap on a locked card picks the tier that opens it
      grid.classList.add('is-init');
      requestAnimationFrame(() => requestAnimationFrame(() => grid.classList.remove('is-init')));
      for (const tile of tiles) tile.addEventListener('click', () => { if (!tile.classList.contains('is-on')) this.#pickTier(tile.dataset.gbFrom); });
    }
    this.#gbReady = true;
    for (const tile of tiles) {
      const from = Number(tile.dataset.gbFrom || 1);
      top = Math.max(top, from);
      if (from <= n) on += 1;
      const was = tile.classList.contains('is-on') || tile.classList.contains('is-shake');
      if (from <= n && !was && !first) {
        // Back Hero unbox: shake, then the card is scratched off and a little burst pops
        tile.classList.add('is-shake');
        setTimeout(() => {
          tile.classList.remove('is-shake');
          if (!tile.isConnected || Number(tile.dataset.gbFrom || 1) > this.tier) return;
          tile.classList.add('is-on', 'is-pop');
          setTimeout(() => tile.classList.remove('is-pop'), 800);
        }, 380);
      } else if (from > n) {
        tile.classList.remove('is-on', 'is-shake', 'is-pop');
      } else if (first) {
        tile.classList.add('is-on');
      }
    }
    this.querySelectorAll('.st-bundle__pips i').forEach((pip, i) => pip.classList.toggle('is-on', i < on));
    const count = this.querySelector('[data-gb-count]');
    if (count) count.textContent = `${on} of ${tiles.length} unlocked`;
    const hint = this.querySelector('[data-gb-hint]');
    if (hint) {
      hint.hidden = !tiles.length;
      if (on < tiles.length) {
        hint.classList.remove('is-all');
        hint.dataset.tier = String(top);
        hint.textContent = 'Choose ';
        const u = document.createElement('u');
        u.textContent = this.#tierLabel(top);
        hint.append(u, ` for all ${tiles.length} gifts`);
      } else {
        hint.classList.add('is-all');
        delete hint.dataset.tier;
        hint.textContent = `All ${tiles.length} free gifts unlocked!`;
      }
    }
    this.#renderSave(n, tiles.filter((t) => Number(t.dataset.gbFrom || 1) <= n)); // by tier, not by class: a card may still be mid-unbox
    this.#syncSticky(n);
  }

  #gbReady = false;

  #pickTier(t) {
    const radio = this.refs.tiers.find((r) => r.value === String(t));
    if (!radio || radio.checked) return;
    radio.checked = true;
    this.#render();
  }

  /** sticky bar (Horizon's, restyled in the block CSS): "Buy 2 · S + M · $157.42" under the product name */
  #syncSticky(n) {
    const line = document.querySelector('sticky-add-to-cart .sticky-add-to-cart__variant');
    if (!line) return;
    const mix = this.#mix(n);
    const text = [this.#tierLabel(n), mix, this.#money(this.#totals(n).now)].filter(Boolean).join(' · ');
    if (line.textContent !== text) line.textContent = text;
    if (!this.#stickyWatch) {
      // Horizon rewrites this line on its own variant events; put ours back
      this.#stickyWatch = new MutationObserver(() => { const t = [this.#tierLabel(this.tier), this.#mix(this.tier), this.#money(this.#totals(this.tier).now)].filter(Boolean).join(' · '); if (line.textContent !== t) line.textContent = t; });
      this.#stickyWatch.observe(line, { childList: true, characterData: true, subtree: true });
    }
  }

  #stickyWatch = null;

  #tierLabel(n) {
    return this.querySelector(`[data-tier="${n}"] .st-bundle__label`)?.textContent.trim() || `Buy ${n}`;
  }

  #renderSave(n, gifts) {
    const box = this.querySelector('[data-save]');
    if (!box) return;
    const { now, was, off } = this.#totals(n);
    const units = this.#chosen(n).length;
    const rows = [];
    const row = (label, right) => rows.push([label, right]);
    const price = now + off;
    row(`${units}× ${this.dataset.productTitle || 'Item'}`, was > price ? [['s', this.#money(was)], ' ', this.#money(price)] : [this.#money(price)]);
    const tc = this.#tierCfg(n);
    if (off > 0) row(`${this.#tierLabel(n)}: ${Number(tc.off || 0) > 0 ? `${this.#money(Number(tc.off))} off each` : `extra ${Number(tc.pct || 0)}% off`}`, [`−${this.#money(off)}`]);
    let giftValue = 0;
    const lid = this.querySelector('[data-unit-gift-id]');
    const lidValue = Number(lid?.dataset.unitGiftValue || 0) * units;
    if (lid && units) {
      giftValue += lidValue;
      row(`Free: ${units}× ${lid.dataset.unitGiftName || 'gift'}`, lidValue ? [['s', this.#money(lidValue)], ' ', ['b', 'FREE']] : [['b', 'FREE']]);
    }
    for (const g of gifts) {
      const v = Number(g.dataset.gbValue || 0);
      giftValue += v;
      row(`Free gift: ${g.dataset.gbName || ''}`, v ? [['s', this.#money(v)], ' ', ['b', 'FREE']] : [['b', 'FREE']]);
    }
    const separately = Math.max(was, price) + giftValue;
    const saved = separately - now;
    box.hidden = saved <= 0;
    const amt = box.querySelector('[data-save-amt]');
    if (amt) amt.textContent = this.#money(saved);
    const body = box.querySelector('[data-save-body]');
    if (!body) return;
    const el = (tag, cls, parts) => {
      const node = document.createElement(tag);
      if (cls) node.className = cls;
      for (const p of parts || []) {
        if (Array.isArray(p)) { const inner = document.createElement(p[0]); inner.textContent = p[1]; node.append(inner); } else node.append(p);
      }
      return node;
    };
    body.replaceChildren(
      ...rows.map(([label, right]) => el('div', 'st-bundle__save-row', [el('span', '', [label]), el('span', '', right)])),
      el('div', 'st-bundle__save-total', [
        el('span', '', ['Separately ', ['s', this.#money(separately)]]),
        el('span', '', ['Today ', el('span', 'st-bundle__save-today', [this.#money(now)])]),
      ])
    );
  }

  /** hint button: jump to the tier that unlocks every gift */
  pickAll(event) {
    const t = event?.currentTarget?.dataset?.tier || this.querySelector('[data-gb-hint]')?.dataset.tier;
    if (t) this.#pickTier(t);
  }

  toggleSave() {
    const box = this.querySelector('[data-save]');
    const top = box?.querySelector('.st-bundle__save-top');
    const body = box?.querySelector('[data-save-body]');
    if (!top || !body) return;
    const open = body.hidden;
    body.hidden = !open;
    top.setAttribute('aria-expanded', String(open));
    const more = box.querySelector('[data-save-more]');
    if (more) more.textContent = open ? 'Hide details' : 'See details';
  }

  /** Add to cart button: "Add 2 to cart - $159.90 →" (+ ticked paid add-ons) */
  #syncButton() {
    if (!this.#form) return;
    const button = this.#form.querySelector('.add-to-cart-button, button[name="add"]');
    if (!button) return;
    const text = button.querySelector('.add-to-cart-text') || button;
    if (!this.#priceEl) {
      // reuse the offer-box price span if it exists, so the two scripts never fight
      this.#priceEl = button.querySelector('.st-atc-price');
      if (!this.#priceEl) { this.#priceEl = document.createElement('span'); this.#priceEl.className = 'st-atc-price'; text.appendChild(this.#priceEl); }
    }
    const n = this.tier;
    let cents = this.#totals(n).now;
    const section = this.closest('.shopify-section') || document;
    for (const box of section.querySelectorAll('st-upsell-component')) {
      if (!box.checked || !box.dataset.variantId || box.classList.contains('st-upsell--gift')) continue;
      try {
        const vid = this.#form.querySelector('input[name="id"]')?.value;
        const combined = JSON.parse(box.dataset.priceCombined || '{}')[vid];
        const base = JSON.parse(box.dataset.priceBase || '{}')[vid];
        if (combined && base) cents += StBundleComponent.toCents(combined) - StBundleComponent.toCents(base);
      } catch {}
    }
    // first real text node of the label (Horizon 3.3 nests it two spans deep), skipping the icon and our price span
    const walker = document.createTreeWalker(text, NodeFilter.SHOW_TEXT, { acceptNode: (node) => node.textContent.trim() && !node.parentElement.closest('.st-atc-price, .svg-wrapper') ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT });
    const labelNode = walker.nextNode();
    if (labelNode) labelNode.textContent = n > 1 ? `${this.dataset.atcLabel || 'Add to cart'} (${n}) ` : `${this.dataset.atcLabel || 'Add to cart'} `;
    this.#priceEl.textContent = ` - ${this.#money(cents)} →`;
  }

  #money(cents) {
    const fmt = this.dataset.moneyFormat || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    const withComma = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/, withComma)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toLocaleString('en-US'))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.'))
      .replace(/<[^>]+>/g, '');
  }

  static toCents(money) {
    const n = String(money).replace(/[^\d.,]/g, '').replace(/,(?=\d{3}\b)/g, '').replace(',', '.');
    return Math.round(parseFloat(n) * 100) || 0;
  }

  /** items for the cart: every chosen size, the tier's gift, and ticked paid add-on boxes */
  cartItems() {
    const n = this.tier;
    const counts = new Map();
    for (const id of this.#chosen(n)) counts.set(id, (counts.get(id) || 0) + 1);
    const items = [...counts].map(([id, quantity]) => ({ id, quantity }));
    // 29/09/2026: gifts are child lines of the product (Shopify nested cart lines): removing the product
    // removes them too. parent_id may only name a variant sent in this same request - the first product line.
    const parent = items[0]?.id ? { parent_id: items[0].id } : {};
    for (const g of this.#gifts(n)) items.push({ id: g.id, quantity: 1, ...parent });
    // one per unit (the pan's glass lid): as many as the units going to the cart
    const unitGift = Number(this.querySelector('[data-unit-gift-id]')?.dataset.unitGiftId || 0);
    const units = this.#chosen(n).length;
    if (unitGift && units) items.push({ id: unitGift, quantity: units, ...parent });
    const section = this.closest('.shopify-section') || document;
    for (const box of section.querySelectorAll('st-upsell-component')) {
      if (box.checked && box.dataset.variantId && !box.classList.contains('st-upsell--gift')) items.push({ id: Number(box.dataset.variantId), quantity: 1 });
    }
    // 29/09/2026: the pre-ticked Shipping Protection rides in this same request - it used to follow as a second
    // request after the drawer opened, and its line popped in about a second later. Not when the shopper switched
    // it off this session, and not when the cart already has it.
    const protection = Number(this.dataset.protectionVariant || 0);
    const toggle = document.querySelector('st-shipping-protection');
    let declined = false;
    try {
      declined = sessionStorage.getItem('st-protection-declined') === '1';
    } catch {}
    const inCart = toggle && toggle.dataset.currentVariant && toggle.dataset.currentVariant !== '0';
    if (protection && !declined && !inCart && items.length) items.push({ id: protection, quantity: 1 });
    return items;
  }

  static onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.dataset.type !== 'add-to-cart-form') return;
    if (form.dataset.stSkipBatch === '1') return; // the offer-box script is retrying the main product alone
    const section = form.closest('.shopify-section') || document;
    const bundle = section.querySelector('st-bundle-component');
    if (!bundle) return;
    const items = bundle.cartItems();
    if (!items.length) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const sectionIds = [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean);
    const button = form.querySelector('button[name="add"]');
    if (button) button.setAttribute('aria-busy', 'true');

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: sectionIds.join(','), sections_url: window.location.pathname }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.status) {
          console.warn('[st-bundle] add rejected:', data.description || data.message);
          form.dispatchEvent(new CartErrorEvent(form.id || '', data.description || data.message || 'Add to cart failed', '', null));
          return;
        }
        const comp = form.closest('product-form-component') || form;
        comp.dispatchEvent(
          new CartAddEvent({}, String(items[0].id), {
            source: 'product-form-component',
            itemCount: items.reduce((s, i) => s + i.quantity, 0),
            productId: comp.dataset?.productId,
            sections: data.sections,
          })
        );
      })
      .catch((error) => {
        console.error('[st-bundle]', error);
        form.dispatchEvent(new CartErrorEvent(form.id || '', error?.message || 'Add to cart failed', '', null));
      })
      .finally(() => {
        if (button) button.removeAttribute('aria-busy');
      });
  };
}

if (!customElements.get('st-bundle-component')) customElements.define('st-bundle-component', StBundleComponent);
